import React, { useEffect, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCPrintContainer, RCCServicesApi, RCCTooltip } from "react-common-components";
import IComponentColumnPagination from "react-common-components/dist/components/Pagination/Column/interface";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import DateTimePicker from "src/components/DateTimePicker";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import Form from "src/components/Form";
import LovReportField from "src/components/Lov/ReportField";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import Select from "src/components/Select";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import yup from "src/utils/yup/extensionMethods";
import { useAuth } from "src/context/Auth/context";
import AppIcons from "src/components/AppIcons";
import { IRefPrintContainer } from "react-common-components/dist/components/PrintContainer/interface";
import downloadFile from "src/utils/downloadFile";
import { bindTypes, IWdtba003, IWdtba010 } from "../Config/interface";
import { paginationsStlyes, PrintStyles, StyledCell } from "./styles";
import { IWdtba009View } from "./interface";
import resolveRules from "./resolveRule";

const ReportView: React.FC = () => {
	const { addToast} = RCCHookToast();
	const { translate,currentLanguage} = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { company } = useAuth();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	
	const [title,setTitle] = useState<string>();
	const [binds,setBinds] = useState<IWdtba009View[]>([]);
	const [queryParameters,setQueryParameters] = useState<IWdtba009View[]>([]);
	const [schema,SetSchema] = useState<any>();
	const [columns,setColumns] = useState<IWdtba003[]>();
	const [results,setResults] = useState<any>();
	const [queryExecuted,setQueryExecuted] = useState(false);
	const [showParameters,setShowParameters] = useState(true);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [printLoading, setPrintLoading] = useState(false);
	const paginationRef = useRef<IForwardPagination>(null);

	const {
		register,
		control,
		formState:{errors},
		setError,
		clearErrors,
		reset,
		handleSubmit
	}= useForm();
	
	const params = useParams<{
        reportId: string;
    }>();

	const printRef = useRef<IRefPrintContainer>(null);
	const executePrint = async() => {
		try {
			setPrintLoading(true);
			const  paginationData = paginationRef.current?.getAllRows() as any;

			if(paginationData?.records?.length !== results.length)
				await paginationRef.current?.loadAllOptions(() => results);
			printRef.current?.print();
			setPrintLoading(false);
		} catch (error) {
			setPrintLoading(false);
		}
	
	};

	const creatRequiredSchema = (filters:IWdtba009View[]) => {
		const baseSchema = {} as any;
		
		filters.forEach((bind) => {
			if(!bind.wda009Obrigatorio)
				return;
			
			let validator;
			switch (String(bind.wda009Tipo)){
			case bindTypes.DATE:
				validator = yup.date()
					.typeError(translate("O campo {{name}} é requerido",{name:bind.wda009Label}))
					.required(translate("O campo {{name}} é requerido",{name:bind.wda009Label}));
				baseSchema[bind.wda009Bind] = validator;
				break;
			case bindTypes.PERIOD:
				validator = yup.date()
					.typeError(translate("O campo {{name}} é requerido",{name:bind.wda009Label}))
					.required(translate("O campo {{name}} é requerido",{name:bind.wda009Label}));
				baseSchema[`${bind.wda009Bind}_INIT`] = validator;
				baseSchema[`${bind.wda009Bind}_END`] = validator;
				break;
			default:
				validator = yup.string()
					.required(translate("O campo {{name}} é requerido",{name:bind.wda009Label}));
				baseSchema[bind.wda009Bind] = validator;
				break;
			}
			
		});
		SetSchema(yup.object().shape(baseSchema));
	};

	const submitParameters = async(data:any) => {
		try{
			setSubmitLoading(true);
			clearErrors();
			const dataForm = {...data};
			


			binds.forEach((bind) => {
				if(typeof dataForm[bind.wda009Bind] === "object"){
					const value = dataForm[bind.wda009Bind]?.value;
					dataForm[bind.wda009Bind] = value;
				}
			});

			await schema.validate({...data}, {
				abortEarly: false
			});

			const changedBinds = binds.map((bind) => {
				let newValue;
				let displayValue;

				switch (bind.wda009Tipo.toString()) {
				case  bindTypes.PERIOD:
					if(!dataForm[`${bind.wda009Bind}_INIT`] || !dataForm[`${bind.wda009Bind}_END`]){
						newValue = null;
					}else{
						newValue = `${formatDate(dataForm[`${bind.wda009Bind}_INIT`])} - ${formatDate(dataForm[`${bind.wda009Bind}_END`])}`;
					}
					break;
				case  bindTypes.DATE:
					newValue = formatDate(dataForm[bind.wda009Bind]) || null;
					break;
				case bindTypes.OPTIONS_LIST:
				case bindTypes.lOV:
					newValue = dataForm[bind.wda009Bind];
					if(typeof data[bind.wda009Bind] === "object"){
						displayValue = data[bind.wda009Bind]?.label;
					}
					break;

				default:
					newValue = dataForm[bind.wda009Bind];
					break;
				}

				const bindValue = {...bind};
				delete bindValue.options;
				delete bindValue.showOptions;
				
				return {
					...bindValue,
					wda009Valordefault:newValue,
					displayValue:displayValue??newValue
				};
			}); 
		
			const response = await RCCServicesApi.postRequest({
				method: "reports/ExecuteReportQuery",
				body:{reportId:params.reportId,binds:changedBinds}
			});
			setResults(response.data);
			setQueryParameters(changedBinds);
			setSubmitLoading(false);

		}catch(err){
			setSubmitLoading(false);
			
			if(getValidationErrors(err, 
				setError
			)){
				return;
			}
			
			standardErrorMessages(err, translate("Erro ao gerar relatório"), addToast);
		}
	};

	const returnPeriodDefault = (defaultValue:string,field:"init"|"end") => {
		if (defaultValue) {
			const dts = defaultValue.split(" - ");
			const value = field === "init" ? dts[0].split("/") : dts[1].split("/");
			return new Date(`${value[1]}/${value[0]}/${value[2]}`);
		}
		return null;
	};

	const returnOptionsDefault = (defaultValue:String,options?:IWdtba010[]) => {
		const option = options?.find((x) => x.wda010Valor === defaultValue);
		return {value:option?.wda010Valor||"",label:option?.wda010Descricao||""};
	};

	const createStyledCell = (row:any,column: IWdtba003) => {
		const {content,style} = resolveRules(row,column,columns||[],currentLanguage);
		return(
			<StyledCell styleInline={style} dangerouslySetInnerHTML={{__html:content??`<div>${row[column.wda003Nomecoluna]}</div>`}}/>
		);
	};

	const formatValues = () => 
		columns?.map((x) => {
			let type;
			switch (x.wda003Tipocoluna) {
			case "N":
				type="number";
				break;
			case "D":
				type= "date";
				break;
			default:
				type="string";
				break;
			}
			return {
				idColumn:x.wda003Nomecoluna,
				nameColumn:x.wda003Label,
				xs:x.wda003Tamanho,
				type,
				format:{date:currentLanguage=== "en" ?"MM/dd/yyyy":"dd/MM/yyyy"},
				cellRenderer:x.rules && x.rules.length > 0 ? (row) => createStyledCell(row,x) : undefined
			} as IComponentColumnPagination;
		});

	const requestReport = async() => { 
		setLoading(true);
		try {
			const {data} = await RCCServicesApi.getRequest({
				method: "reports/Select",
				urlParameters:{id:params.reportId}	
			});
			
			setTitle(data.wda002Titulo);
			setBinds(data.binds);
			creatRequiredSchema(data.binds);
			setColumns(data.columns);
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao encontrar relatório"), addToast);
			setLoading(false);
		}
	};

	const exportReport = async() => {
		try {
			const response = await RCCServicesApi.postRequest({
				method: "reports/ExportExcel",
				body:{
					reportId:params.reportId,
					currentLanguage,
					binds:queryParameters
				}
			});
	
			downloadFile(response.data, `${title}.csv`, true);
	
			addToast({
				color: "green",
				description: translate("Relatório exportado com sucesso"),
				title: translate("Sucesso")
			});

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao exportar relatório"), addToast);
		}
	};

	useEffect(() => {
		reset();
		requestReport();
	}, []);

	useEffectSkipInitialRender(() => {
		setQueryExecuted(true);
		setShowParameters(false);
		paginationRef.current?.reload();
	}, [results]);
	
	return(<>
		<RCCPrintContainer
			ref={printRef}
			styleInline={PrintStyles}
			documentTitle={title}
		>	
			<RCCGrid
				container
				item
				direction="column"
			>
				<RCCGrid container item className="onlyPrintElement" wrap="nowrap" justifyContent="space-evenly">
					{company.efa002Imagem && (
						<img src={`${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_empresas/${company.efa002Imagem}`} alt="Company Logo" />
					)}
					<Title color="black" size="xl" bold >
						{title}
					</Title>
				</RCCGrid>
				<RCCGrid container item className="noPrintElement">
					<PageTitle
						title={title||""}
						icon={() => <AppIcons name="reports" />}		
					>
						{queryExecuted && 
							<RCCTooltip
								text={translate("Filtros")}
								color={showParameters? "blue" : "grey"}
							>
								<RCCButton
									icon={{name:"filter"}}
									color={showParameters? "blue" : "grey"}
									layout="isRounded"
									size="md"
									onClick={() => setShowParameters((prevState) => !prevState)}
								/>
							</RCCTooltip>
						}
					</PageTitle>	
				</RCCGrid>
				{showParameters && 
					<RCCGrid container item className="noPrintElement">
						<Title color="purple" size="lg" marginTop={20}>{translate("Filtros")}</Title>
						<Form onSubmit={handleSubmit(submitParameters)}>
							<RCCGrid container item justifyContent="center">
								<RCCGrid container item xs={7} justifyContent="center">
									{binds.map((bind) => (
										<RCCGrid container item key={bind.id}>
											{bind.wda009Tipo.toString() === bindTypes.FREE &&
												<RCCInput
													label={translate(bind.wda009Label)}
													name={bind.wda009Bind}
													defaultValue={bind.wda009Valordefault}
													required={bind.wda009Obrigatorio}
													maxLength={120}
													register={register}
													error={errors[bind.wda009Bind]}
												/>
											}
											{bind.wda009Tipo.toString() === bindTypes.DATE &&
												<DateTimePicker
													control={control}
													label={translate(bind.wda009Label)}
													name={bind.wda009Bind}
													defaultValue={bind.wda009Valordefault}
													required={bind.wda009Obrigatorio}
													type="datePicker"
													error={errors[bind.wda009Bind]}
												/>
											}
											{bind.wda009Tipo.toString() === bindTypes.PERIOD &&
												<DateTimePickerRange
													control={control}
													label={translate(bind.wda009Label)}
													nameInit={`${bind.wda009Bind}_INIT`}
													nameEnd={`${bind.wda009Bind}_END`}
													defaultValueInit={returnPeriodDefault(bind.wda009Valordefault,"init")}
													defaultValueEnd={returnPeriodDefault(bind.wda009Valordefault,"end")}
													required={bind.wda009Obrigatorio}
													type="datePicker"
													error={errors[`${bind.wda009Bind}_INIT`]||errors[`${bind.wda009Bind}_END`]}
												/>	
											}
											{bind.wda009Tipo.toString() === bindTypes.OPTIONS_LIST &&
												<Select
													control={control}
													label={translate(bind.wda009Label)}
													name={bind.wda009Bind}
													defaultValue={returnOptionsDefault(bind.wda009Valordefault,bind.options)}
													required={bind.wda009Obrigatorio}
													isClearable={!bind.wda009Obrigatorio}
													options={bind.options?.map((opt) => ({value:opt.wda010Valor,label:opt.wda010Descricao}))||[]}
													error={errors[bind.wda009Bind]}
													submit="value and label"
												/>
											}
											{bind.wda009Tipo.toString() === bindTypes.lOV &&
												<LovReportField
													control={control}
													label={translate(bind.wda009Label)}
													name={bind.wda009Bind}
													defaultValue = {bind.wda009Valordefault}
													required={bind.wda009Obrigatorio}
													isClearable={!bind.wda009Obrigatorio}
													extraParametersOnLoad={{
														reportId: bind.id
													}}
													error={errors[bind.wda009Bind]}
													submit="value and label"
												/>
											}
										</RCCGrid>
									))}
									{title && 
										<RCCButton size="md" 
											type="submit"
											color="green"
											loading={submitLoading}
											loadingText={translate("Carregando")}
											style={{marginTop:"15px"}}
										> 
											{translate("Gerar relatório")} 
										</RCCButton>  
									}
								</RCCGrid>
							</RCCGrid>		
						</Form>
					</RCCGrid>
				}
				{queryExecuted && 
					<RCCGrid container item direction="column">
						<RCCGrid container item className="onlyPrintElement">
							<table> 
								<thead> 
									<tr> 
										<th>Variável</th> 
										<th>Valor</th> 
									</tr> 
								</thead >  
								<tbody>
									{queryParameters.map((x) => (
										<tr key={x.id}> 
											<td> {x.wda009Label} </td > 
											<td> {x.displayValue}</td > 
										</tr>
									))}
								</tbody>
							</table> 
						</RCCGrid>
						<RCCGrid container item>
							<RCCGrid item container justifyContent="flex-end" className="noPrintElement">
								<RCCTooltip
									text={translate("Imprimir")}
									color="grey"
								>
									<RCCButton
										icon={{name:"print"}}
										color="grey"
										layout="isRounded"
										size="md"
										onClick={() => executePrint()}
										loading={printLoading}
									/>
								</RCCTooltip>
								<RCCTooltip
									text={translate("Exportar")}
									color="grey"
								>
									<RCCButton
										icon={{name:"documentXLSX"}}
										color="grey"
										layout="isRounded"
										size="md"
										onClick={() => exportReport()}
									/>
								</RCCTooltip>
							</RCCGrid>
							<Pagination
								columns={formatValues()||[]}
								ref={paginationRef}
								dataOrRoute={results}
								styleInline={paginationsStlyes}
								renderType="infiniteScroll"
								optionsPerPage={[
									{ value: 100, description: "100" }
								]}
							/>
						</RCCGrid>
					</RCCGrid>	
				}
			</RCCGrid>
		</RCCPrintContainer>
	</>);
};	

export default ReportView;
